import { useNavigate } from "@tanstack/react-router";
import { IconSquareRoundedX } from "@tabler/icons-react";
import { Button, Card, useConfirmDialog } from "@/components/ui";
import { useAuthStore } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { useCancelarSolicitud } from "@/features/solicitudes/hooks/use-cancelar-solicitud";
import { ESTADOS_CANCELABLES_POR_SOLICITANTE } from "@/features/solicitud-detalle/utils/estado-flags";
import type { SolicitudDetalle } from "@/lib/types";

/**
 * Card de la sidebar derecha · solo aparece para el solicitante creador
 * de la solicitud cuando está en un estado cancelable. Abre una
 * confirmación simple (sin motivo · pre-autorización no lo requiere) y
 * dispara la mutation al confirmar.
 */
export function CancelarSolicitudCard({ sol }: { sol: SolicitudDetalle }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const confirm = useConfirmDialog();

  const cancelar = useCancelarSolicitud(sol.id, {
    onSuccess: () => navigate({ to: "/solicitudes", replace: true }),
  });

  // Hotspot 90 · por PERMISO (`solicitudes.gestionar`, el que exige el endpoint) + ser el
  // dueño (lo exige el dominio), no por el rol Solicitante.
  const puedeGestionar = user?.permisos?.includes(PERMISOS.solicitudesGestionar) ?? false;
  const esDueno = user?.id === sol.solicitanteId;
  const cancelable = ESTADOS_CANCELABLES_POR_SOLICITANTE.has(sol.estado);
  if (!puedeGestionar || !esDueno || !cancelable) return null;

  const confirmarCancelacion = async () => {
    const ok = await confirm({
      title: "Cancelar solicitud",
      message: (
        <>
          ¿Seguro que deseas cancelar esta solicitud? Esta acción es <b>terminal</b> ·
          si necesitas reanudar el pago tendrás que iniciar una solicitud nueva.
        </>
      ),
      confirmLabel: "Cancelar solicitud",
      cancelLabel: "Volver",
      confirmColor: "peligro",
    });
    if (ok) cancelar.mutate([]);
  };

  return (
    <Card padding="sm">
      <Button
        variant="subtle"
        className="w-full text-peligro-600 hover:bg-peligro-50 dark:hover:bg-peligro-900/40"
        leftSection={<IconSquareRoundedX size={14} />}
        onClick={confirmarCancelacion}
        loading={cancelar.isPending}
        size="xs"
      >
        Cancelar solicitud
      </Button>
    </Card>
  );
}
